import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const AuditAssurancePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-bold text-[#3E2723] mb-6 border-b-2 border-dotted border-gray-300 pb-6">
            Audit & Assurance
          </h1>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          
          {/* Globally Aligned Audit Framework */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Globally Aligned Audit Framework
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg">
              As a global audit firm, we place the highest importance on integrity. Independence, professional ethics, and confidentiality are embedded across every stage of the engagement, safeguarding objectivity and reinforcing client trust. All audit opinions are issued by licensed CPAs who adhere strictly to AICPA independence and ethical requirements.
            </p>
          </div>

          {/* Audit Procedures */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Audit Procedures:
            </h2>
            <div className="flex items-start">
              <span className="text-[#3E2723] mr-3 mt-1.5 flex-shrink-0">•</span>
              <p className="text-gray-700 leading-relaxed text-lg">
                All audit procedures, from planning through completion, are executed in alignment with AICPA Auditing Standards (AU-C), ensuring consistent adherence to applicable technical and regulatory requirements throughout the audit lifecycle.
              </p>
            </div>
          </div>

          {/* Quality Control Framework */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Quality Control Framework:
            </h2>
            <div className="space-y-6">
              <div className="flex items-start">
                <span className="text-[#3E2723] mr-3 mt-1.5 flex-shrink-0">•</span>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Audit workpapers are prepared, maintained, and retained in line with AICPA documentation standards, supporting a transparent and defensible audit trail.
                </p>
              </div>
              <div className="flex items-start">
                <span className="text-[#3E2723] mr-3 mt-1.5 flex-shrink-0">•</span>
                <p className="text-gray-700 leading-relaxed text-lg">
                  A multi-level review and approval framework is embedded within the workpaper process, comprising preparer and reviewer reviews, followed by engagement partner oversight.
                </p>
              </div>
              <div className="flex items-start">
                <span className="text-[#3E2723] mr-3 mt-1.5 flex-shrink-0">•</span>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Final Audit reports and opinions are issued only after completion and review of the Financial Statements and are signed off by a licensed CPA.
                </p>
              </div>
            </div>
          </div>

          {/* Engagement Team */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Engagement Team:
            </h2>
            <div className="space-y-6">
              <div className="flex items-start">
                <span className="text-[#3E2723] mr-3 mt-1.5 flex-shrink-0">•</span>
                <p className="text-gray-700 leading-relaxed text-lg">
                  All teams consist of experienced auditors with domain-specific expertise, supported by technical specialists where required.
                </p>
              </div>
              <div className="flex items-start">
                <span className="text-[#3E2723] mr-3 mt-1.5 flex-shrink-0">•</span>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Continuous training, regulatory updates, and internal technical support ensure teams remain aligned with evolving professional standards and industry best practices.
                </p>
              </div>
            </div>
          </div>

          {/* Audit Deliverables */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Audit Deliverables:
            </h2>
            <div className="space-y-6">
              <div className="flex items-start">
                <span className="text-[#3E2723] mr-3 mt-1.5 flex-shrink-0">•</span>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Comprehensive audit report detailing findings, opinions, and recommendations.
                </p>
              </div>
              <div className="flex items-start">
                <span className="text-[#3E2723] mr-3 mt-1.5 flex-shrink-0">•</span>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Management Letter highlighting control deficiencies, process improvement opportunities, and actionable recommendations.
                </p>
              </div>
              <div className="flex items-start">
                <span className="text-[#3E2723] mr-3 mt-1.5 flex-shrink-0">•</span>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Regulatory filings and compliance support where applicable.
                </p>
              </div>
            </div>
          </div>

          {/* Engagement Approach */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Engagement Approach:
            </h2>
            <p className="text-gray-700 leading-relaxed text-lg mb-6">
              Our approach is risk-based, efficient, and designed to add value beyond compliance:
            </p>
            <div className="space-y-6">
              <div className="flex items-start">
                <span className="text-[#3E2723] mr-3 mt-1.5 flex-shrink-0">•</span>
                <div>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    <span className="font-semibold">Planning & Risk Assessment:</span> Deep understanding of business operations, internal controls, and financial reporting processes to tailor audit procedures accordingly.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-[#3E2723] mr-3 mt-1.5 flex-shrink-0">•</span>
                <div>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    <span className="font-semibold">Testing & Evidence Gathering:</span> Substantive procedures, analytical reviews, and control testing executed with precision and professional skepticism.
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <span className="text-[#3E2723] mr-3 mt-1.5 flex-shrink-0">•</span>
                <div>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    <span className="font-semibold">Reporting & Communication:</span> Clear, transparent reporting with ongoing dialogue throughout the engagement to address findings proactively.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-[#3E2723] rounded-lg p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">
              Need professional audit services?
            </h2>
            <p className="text-stone-200 mb-8 text-lg">
              Contact us to discuss your audit and assurance needs.
            </p>
            <Button className="bg-white text-[#3E2723] hover:bg-stone-100 px-10 py-6 text-lg shadow-lg">
              Schedule a Consultation
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AuditAssurancePage;
